import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../../services/api';
import { notify } from 'messages';
import { flattenQuery } from 'helpers/flattenQuery';
import { FilterConfig } from 'hooks/useListQuery';
import { Integration } from './integrationsSlice';

export interface IntegrationNode {
  node_id: number;
  integration_id: number;
  name: string;
  description?: string | null;
  zone_id: number;
  zone_name: string;
  http_method: string;
  endpoint_path?: string | null;
  override_url?: string | null;
  use_override_url: boolean;
  inherit_auth: boolean;
  auth_basic: boolean;
  auth_api_token: boolean;
  auth_oauth2: boolean;
  parameters: any[];
  headers: any[];
  created_at?: string;
  updated_at?: string;
}

interface PaginationState {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

interface IntegrationNodesState {
  nodes: IntegrationNode[];
  parentIntegration: Integration | null;
  pagination: PaginationState;
  status: string | null;
  error: string | null;
  config: { header_key: string; label: string; header_type: string }[];
  filtersConfig: FilterConfig[];
  loading: {
    getIntegrationNodes: boolean;
    createIntegrationNode: boolean;
    updateIntegrationNode: boolean;
    deleteIntegrationNode: boolean;
    bulkDeleteIntegrationNodes: boolean;
  };
}

const initialState: IntegrationNodesState = {
  nodes: [],
  parentIntegration: null,
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 2000,
  },
  status: null,
  error: null,
  config: [],
  filtersConfig: [],
  loading: {
    getIntegrationNodes: false,
    createIntegrationNode: false,
    updateIntegrationNode: false,
    deleteIntegrationNode: false,
    bulkDeleteIntegrationNodes: false,
  },
};

// Fetch nodes for an integration (paginated)
export const getIntegrationNodes = createAsyncThunk('integrationNodes/getIntegrationNodes', async (params: { integrationId: number } & Record<string, any>) => {
  const { integrationId, ...queryParams } = params;
  const response = await api.get(`/integrations/${integrationId}/nodes`, {
    params: flattenQuery({
      limit: initialState.pagination.pageSize,
      ...queryParams,
    }),
  });
  return response.data;
});

// Create Node
export const createIntegrationNode = createAsyncThunk<
  IntegrationNode,
  { integrationId: number; data: Partial<IntegrationNode> },
  { rejectValue: { message: string; error?: string } }
>('integrationNodes/createIntegrationNode', async ({ integrationId, data }, { rejectWithValue }) => {
  try {
    const response = await api.post(`/integrations/${integrationId}/nodes`, data);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: err.message });
  }
});

// Update Node
export const updateIntegrationNode = createAsyncThunk<
  IntegrationNode,
  { integrationId: number; nodeId: number; data: Partial<IntegrationNode> },
  { rejectValue: { message: string; error?: string } }
>('integrationNodes/updateIntegrationNode', async ({ integrationId, nodeId, data }, { rejectWithValue }) => {
  try {
    const response = await api.put(`/integrations/${integrationId}/nodes/${nodeId}`, data);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: err.message });
  }
});

// Delete Node
export const deleteIntegrationNode = createAsyncThunk<
  { message: string },
  { integrationId: number; nodeId: number },
  { rejectValue: { message: string; error?: string } }
>('integrationNodes/deleteIntegrationNode', async ({ integrationId, nodeId }, { rejectWithValue }) => {
  try {
    const response = await api.delete(`/integrations/${integrationId}/nodes/${nodeId}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: err.message });
  }
});

// Bulk Delete Nodes
export const bulkDeleteIntegrationNodes = createAsyncThunk<
  { message: string },
  { integrationId: number; nodeIds: number[] },
  { rejectValue: { message: string; error?: string } }
>('integrationNodes/bulkDeleteIntegrationNodes', async ({ integrationId, nodeIds }, { rejectWithValue }) => {
  try {
    const response = await api.delete(`/integrations/${integrationId}/nodes`, {
      data: { node_ids: nodeIds },
    });
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: err.message });
  }
});

const integrationNodesSlice = createSlice({
  name: 'integrationNodes',
  initialState,
  reducers: {
    clearIntegrationNodes: (state) => {
      state.nodes = [];
      state.parentIntegration = null;
      state.status = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ================== getIntegrationNodes ==================
      .addCase(getIntegrationNodes.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.getIntegrationNodes = true;
      })
      .addCase(getIntegrationNodes.fulfilled, (state, action) => {
        state.status = 'succeeded';

        const { nodes, integration, config, filtersConfig, pagination } = action.payload;

        state.nodes = nodes;
        state.parentIntegration = integration;
        state.config = config;
        state.filtersConfig = filtersConfig || [];

        state.pagination.totalItems = pagination?.total_records ?? 0;
        state.pagination.totalPages = pagination?.total_pages ?? 0;
        state.pagination.pageSize = pagination?.limit ?? state.pagination.pageSize;
        state.pagination.currentPage = (pagination?.current_page ?? 1) > (pagination?.total_pages ?? 0) ? 1 : (pagination?.current_page ?? 1);

        state.loading.getIntegrationNodes = false;
      })
      .addCase(getIntegrationNodes.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch integration nodes';
        state.loading.getIntegrationNodes = false;
      });

    builder
      // ================== createIntegrationNode ==================
      .addCase(createIntegrationNode.pending, (state) => {
        state.loading.createIntegrationNode = true;
        state.error = null;
      })
      .addCase(createIntegrationNode.fulfilled, (state, action) => {
        state.nodes.push(action.payload);
        state.status = 'succeeded';
        state.loading.createIntegrationNode = false;

        const messageKey = 'data-created-integration-node';
        if (!notify.isShowing(messageKey)) {
          notify.success('Integration node created successfully!', { key: messageKey });
        }
      })
      .addCase(createIntegrationNode.rejected, (state, action) => {
        state.loading.createIntegrationNode = false;
        state.status = 'failed';

        const payload = action.payload as { message?: string; error?: string } | undefined;

        state.error = payload?.error || payload?.message || 'Failed to create node';

        const messageKey = 'data-failed-to-create-integration-node';
        if (!notify.isShowing(messageKey)) {
          notify.error(`Failed to create node! ${state.error}`, { key: messageKey });
        }
      })

      // ================== updateIntegrationNode ==================
      .addCase(updateIntegrationNode.pending, (state) => {
        state.loading.updateIntegrationNode = true;
        state.error = null;
      })
      .addCase(updateIntegrationNode.fulfilled, (state, action) => {
        const index = state.nodes.findIndex((n) => n.node_id === action.payload.node_id);
        if (index !== -1) {
          state.nodes[index] = action.payload;
        }
        state.status = 'succeeded';
        state.loading.updateIntegrationNode = false;

        const messageKey = 'data-updated-integration-node';
        if (!notify.isShowing(messageKey)) {
          notify.success('Integration node updated successfully!', { key: messageKey });
        }
      })
      .addCase(updateIntegrationNode.rejected, (state, action) => {
        state.loading.updateIntegrationNode = false;
        state.status = 'failed';

        const payload = action.payload as { message?: string; error?: string } | undefined;

        state.error = payload?.error || payload?.message || 'Failed to update node';

        const messageKey = 'data-failed-to-update-integration-node';
        if (!notify.isShowing(messageKey)) {
          notify.error(`Failed to update the node. Message: ${state.error}`, {
            key: messageKey,
          });
        }
      })

      // ================== deleteIntegrationNode ==================
      .addCase(deleteIntegrationNode.pending, (state) => {
        state.loading.deleteIntegrationNode = true;
        state.error = null;
      })
      .addCase(deleteIntegrationNode.fulfilled, (state, action) => {
        state.nodes = state.nodes.filter((n) => n.node_id !== action.meta.arg.nodeId);
        state.status = 'succeeded';
        state.loading.deleteIntegrationNode = false;

        const messageKey = 'data-deleted-integration-node';
        if (!notify.isShowing(messageKey)) {
          notify.success('Node deleted successfully!', { key: messageKey });
        }
      })
      .addCase(deleteIntegrationNode.rejected, (state, action) => {
        state.loading.deleteIntegrationNode = false;
        state.status = 'failed';

        const payload = action.payload as { message?: string; error?: string } | undefined;

        state.error = payload?.error || payload?.message || 'Failed to delete node';

        const messageKey = 'data-failed-to-delete-integration-node';
        if (!notify.isShowing(messageKey)) {
          notify.error(`Failed to delete the node! ${state.error}`, { key: messageKey });
        }
      })

      // ================== bulkDeleteIntegrationNodes ==================
      .addCase(bulkDeleteIntegrationNodes.pending, (state) => {
        state.loading.bulkDeleteIntegrationNodes = true;
        state.error = null;
      })
      .addCase(bulkDeleteIntegrationNodes.fulfilled, (state, action) => {
        const deletedIds = action.meta.arg.nodeIds;
        state.nodes = state.nodes.filter((n) => !deletedIds.includes(n.node_id));
        state.status = 'succeeded';
        state.loading.bulkDeleteIntegrationNodes = false;

        const messageKey = 'data-bulk-deleted-integration-nodes';
        if (!notify.isShowing(messageKey)) {
          notify.success('Nodes deleted successfully!', { key: messageKey });
        }
      })
      .addCase(bulkDeleteIntegrationNodes.rejected, (state, action) => {
        state.loading.bulkDeleteIntegrationNodes = false;
        state.status = 'failed';

        const payload = action.payload as { message?: string; error?: string } | undefined;

        state.error = payload?.error || payload?.message || 'Failed to delete nodes';

        const messageKey = 'data-failed-to-bulk-delete-integration-nodes';
        if (!notify.isShowing(messageKey)) {
          notify.error(`Failed to delete nodes! ${state.error}`, { key: messageKey });
        }
      });
  },
});

export const { clearIntegrationNodes } = integrationNodesSlice.actions;
export default integrationNodesSlice.reducer;
